import { EventListeners, MinzeElement, Reactive, Attrs } from 'minze'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'

Locale.getInstance().registerKeySet('fr', 'lvc-contribution-type-field-all', {
  title: 'Type de contribution*',
  choose: 'Choisir',
})

Locale.getInstance().registerKeySet('en', 'lvc-contribution-type-field-all', {
  title: 'Contribution type*',
  choose: 'Choose',
})

// Contribution types with FR/EN labels
// The id (value) is the French text as expected by the database
const CONTRIBUTION_TYPES_ALL = [
  { id: 'Artiste', name: { fr: 'Artiste', en: 'Artist' } },
  { id: 'Auteur', name: { fr: 'Auteur', en: 'Author' } },
  { id: 'Équipe de création', name: { fr: 'Équipe de création', en: 'Creative Team' } },
  { id: 'Personnel technique', name: { fr: 'Personnel technique', en: 'Technical Staff' } },
  { id: 'Direction artistique', name: { fr: 'Direction artistique', en: 'Artistic Direction' } },
  { id: 'Mise en scène', name: { fr: 'Mise en scène', en: 'Director' } },
  { id: 'Producteur', name: { fr: 'Producteur', en: 'Producer' } },
  { id: 'Organisateur', name: { fr: 'Organisateur', en: 'Organizer' } },
  { id: 'Promoteur', name: { fr: 'Promoteur', en: 'Promoter' } },
  { id: 'Commanditaire', name: { fr: 'Commanditaire', en: 'Sponsor' } },
  { id: 'Autre', name: { fr: 'Autre', en: 'Other' } },
]

export interface LvcContributionTypeFieldAll {
  title: string
  placeholder: string
  open: boolean
  onChange: string
  defaultValue: any
  value: string
  error: string
}

export class LvcContributionTypeFieldAll extends MinzeElement {
  private contributionTypes: typeof CONTRIBUTION_TYPES_ALL = CONTRIBUTION_TYPES_ALL
  private selectedId: string = ''
  private filter: string = ''
  attrs: Attrs = ['title', 'placeholder', ['default-value', ''], ['on-change', ''], ['error', '']]

  reactive: Reactive = [['value', '']]

  static observedAttributes = ['title', 'on-change', 'default-value', 'error']

  async onBlur(e: Event): Promise<void> {
    this.open = false
    this.rerender()
  }

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'default-value' && oldValue !== newValue) {
      this.updateDefaultValue()
    }
  }

  updateDefaultValue() {
    if (this.defaultValue && this.defaultValue !== true) {
      try {
        const parsed = JSON.parse(decodeURIComponent(this.defaultValue))
        if (Array.isArray(parsed) && parsed.length > 0) {
          this.selectedId = parsed[0].id || ''
        } else if (typeof parsed === 'string') {
          this.selectedId = parsed
        }
      } catch (e) {
        this.selectedId = this.defaultValue
      }
    }
    this.rerender()
  }

  async onReady() {
    if (this.defaultValue && this.defaultValue !== true) {
      this.updateDefaultValue()
    }
    this.rerender()
  }

  async onToggle() {
    this.open = !this.open
    this.rerender()
  }

  onContributionTypeClick(e: Event): void {
    let value = (e.target as HTMLElement).getAttribute('data-id')

    if (!value) {
      return
    }

    this.selectedId = value
    this.serializeValue()
    this.onToggle()
    this.rerender()
  }

  async onFilterChange(e: Event) {
    this.filter = (e.target as HTMLInputElement).value
    this.rerender()
  }

  serializeValue() {
    let selectedContributionType = this.selectedId
    this.value = encodeURIComponent(JSON.stringify(selectedContributionType))
    Callback.call(this.onChange, selectedContributionType)
    this.cast('change', selectedContributionType)
  }

  getContributionTypesLabel() {
    if (!this.selectedId) {
      return Locale.getKey('lvc-contribution-type-field-all.choose')
    }

    const selectedContributionType = this.contributionTypes.find(
      contributionType => contributionType.id === this.selectedId
    )

    if (selectedContributionType) {
      return selectedContributionType.name[Locale.language as 'fr' | 'en']
    }

    return ''
  }

  renderContributionType = (contributionType: (typeof CONTRIBUTION_TYPES_ALL)[0]) => {
    const label = contributionType.name[Locale.language as 'fr' | 'en']
    if (this.filter && label.toLowerCase().indexOf(this.filter.toLowerCase()) === -1) {
      return ''
    }

    let isNotSelected = this.selectedId !== contributionType.id

    return `
      <button 
        name="contributionType"
        class="${isNotSelected ? 'not-selected' : 'selected'}" 
        data-id="${contributionType.id}">
        ${label}
        ${isNotSelected ? '' : '<lvc-circle-check-select-icon></lvc-circle-check-select-icon>'}
      </button>
    `
  }

  renderContributionTypes = () => {
    let html = ''

    for (let i = 0; i < this.contributionTypes.length; i++) {
      html += this.renderContributionType(this.contributionTypes[i])
    }

    return html
  }

  html = () => {
    let showError = typeof this.error === 'string' && this.error.length > 0
    return `
      <div class="title">${this.title || Locale.getKey('lvc-contribution-type-field-all.title')}</div>
      <div class="input ${this.selectedId ? 'has-value' : ''} ${showError ? 'error' : ''}">
        <button class="dropbtn" name="toggle">
          ${this.getContributionTypesLabel()}
          <lvc-chevron-down-icon></lvc-chevron-down-icon>
        </button>
        <div class="overlay ${this.open ? 'show' : ''}" name="overlay"></div>
        <div class="dropdown-content ${this.open ? 'show' : ''}">
          <div class="header">
            <span>${Locale.getKey('lvc-contribution-type-field-all.choose')}</span>
            <lvc-chevron-up-icon></lvc-chevron-up-icon>
          </div>
          <div class="contributionTypes" data-selected="${this.selectedId}">
            ${this.renderContributionTypes()}
          </div>
        </div> 
      </div>
      ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
    `
  }

  css = () => {
    return `
      :host {
        width: 100%;
        min-width: 150px;
      }

      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: var(--Ciel, #BFF0FF);

      }


      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }

      .title {
        color: var(--Minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;

        /* Body 3 */
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        margin-bottom: 3.5px;
      }

      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        border: 1px solid var(--Minuit, #1E007D);
        position: relative;
        border-radius: 24px;
      }
      
      .input.has-value {
        border: 1px solid rgba(30, 0, 125, 0.3);
        background: #BFF0FF;
      }

      .input.error {
        background: #FFEBD7;
        border-color: #FF4600;
      }

      .contributionTypes {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }


      .contributionTypes button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: fit-content;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }

      .contributionTypes button:last-child {
        border-bottom: none;
      }

      .contributionTypes button.selected {
      }


      /* style.css */

      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 12px 16px;
        font-family: ${Font.family};
        font-size: 16px;
        font-weight: ${Font.weight.medium};
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }

      /* Dropdown Content */
      .dropdown-content {
        width: calc(100% + 2px);
        margin: -1px;
        display: none;
        position: absolute;
        background-color: #f9f9f9;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px; 
        max-height: 300px;
        min-height: 96px;
        border: 1px solid var(--Minuit, #1E007D);
        line-height: 32px;
      }

      .dropdown-content.show {
        display: block;
      }


    `
  }

  eventListeners: EventListeners = [
    ['button[name="toggle"]', 'click', this.onToggle.bind(this)],
    ['button[name="contributionType"]', 'click', this.onContributionTypeClick.bind(this)],
    ['input[name="filter"]', 'keyup', this.onFilterChange.bind(this)],
    [this, 'blur', this.onBlur.bind(this)],
    ['div.overlay', 'click', this.onBlur.bind(this)],
  ]
}
